import { InputWithLabel } from '@adotel/components'
import { Switch } from '@material-ui/core'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import IconButton from '@material-ui/core/IconButton'
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'
import SearchIcon from '@material-ui/icons/Search'
import { matchSorter } from 'match-sorter'
import React, { useState, FC, useEffect } from 'react'

import CustomCheckBox from '../../../../components/CustomCheckBox'
import { convertPersianNumbersToEnglishNumbers } from '../../../../helpers/numbers'
import { getTranslate, lang } from '../../../../localization'
import { CheckBoxDataType } from '../../../../types/customCheckBox'
import {
  Entities,
  RoomForSearch,
  SearchAvailabilityInput
} from '../../../../types/searchAvailability'
import PriceSlider from '../PriceSlider'
import ContentFitCheckboxList from './CheckBoxList'
import useStyles from './styles/index.styles'

interface FilterDrawerProps {
  open: boolean
  onClose: () => void
  onOpen: () => void
  searchData: Entities | undefined
  searchAvailabilityInput: SearchAvailabilityInput
  setSearchAvailabilityInput: React.Dispatch<
    React.SetStateAction<SearchAvailabilityInput>
  >
  setFilteredRoomType: React.Dispatch<React.SetStateAction<RoomForSearch[]>>
  setselectedRoomTypes: React.Dispatch<React.SetStateAction<string[]>>
  selectedRoomTypes: string[]
  setSelectedRoomFacilities: React.Dispatch<React.SetStateAction<string[]>>
  selectedRoomFacilities: string[]
  priceRange: number[]
  setSelectedProviders: React.Dispatch<React.SetStateAction<string[]>>
  selectedProviders: string[]
  setPriceRange: React.Dispatch<React.SetStateAction<number[]>>
  isUserPriceChange: React.MutableRefObject<boolean>
  maxPriceRange: number
  initialData: {
    uniqueRoomFacilities: CheckBoxDataType[]
    uniqueProviders: CheckBoxDataType[]
    roomTypeNames: {
      label: string
      value: string
    }[]
  } | null
  setInitialData: React.Dispatch<
    React.SetStateAction<{
      uniqueRoomFacilities: CheckBoxDataType[]
      uniqueProviders: CheckBoxDataType[]
      roomTypeNames: {
        label: string
        value: string
      }[]
    } | null>
  >
}

const FilterDrawer: FC<FilterDrawerProps> = ({
  open,
  onClose,
  onOpen,
  searchData,
  searchAvailabilityInput,
  setSearchAvailabilityInput,
  setselectedRoomTypes,
  selectedRoomTypes,
  selectedRoomFacilities,
  setSelectedRoomFacilities,
  selectedProviders,
  setSelectedProviders,
  priceRange,
  setPriceRange,
  isUserPriceChange,
  setFilteredRoomType,
  maxPriceRange,
  initialData,
  setInitialData
}) => {
  const classes = useStyles()

  const min = 0

  const [searchInputValue, setsearchInputValue] = useState<string>('')

  const handlePriceChange = (newRange: number[]) => {
    isUserPriceChange.current = true
    setPriceRange(newRange)
  }
  useEffect(() => {
    isUserPriceChange.current = false
    setPriceRange([min, maxPriceRange])
  }, [min, maxPriceRange])

  const handleResetAll = () => {
    setSelectedRoomFacilities([])
    setselectedRoomTypes([])
    setPriceRange([min, maxPriceRange])
  }

  useEffect(() => {
    const fullRoomTypes = searchData?.rooms || []
    const searchfilteredRoomTypes = searchInputValue
      ? matchSorter(fullRoomTypes, searchInputValue, {
          keys: ['roomTypeName', 'roomTypeCode']
        })
      : fullRoomTypes

    if (selectedRoomTypes.length === 0 || selectedRoomTypes.includes('All')) {
      setFilteredRoomType(searchfilteredRoomTypes)
    } else {
      const filtered = searchfilteredRoomTypes.filter((room) =>
        selectedRoomTypes.includes(room.roomTypeCode)
      )
      setFilteredRoomType(filtered)
    }
  }, [searchInputValue, selectedRoomTypes, searchData])

  useEffect(() => {
    if (!initialData && searchData?.rooms) {
      const facilities: { code: string; label: string }[] = []
      const providers: { code: string; label: string }[] = []
      const roomTypeNames = [{ label: 'All', value: 'All' }]

      searchData.rooms.forEach((room) => {
        roomTypeNames.push({
          value: room.roomTypeCode,
          label: room.roomTypeName
        })
        room.facilities.forEach((f) =>
          facilities.push({ code: f.ID, label: f.Name })
        )
        room.ratePlans.forEach((rp) =>
          providers.push({
            code: rp.displayProviderCode,
            label: rp.providerName
          })
        )
      })

      const uniqueRoomFacilities = Array.from(
        new Map(facilities.map((f) => [f.code, f])).values()
      ).map((d) => ({
        title: d.label,
        value: d.code,
        checked: false
      }))

      const uniqueProviders = Array.from(
        new Map(providers.map((p) => [p.code, p])).values()
      ).map((d) => ({
        title: d.label,
        value: d.code,
        checked: false
      }))
      setInitialData({ uniqueRoomFacilities, uniqueProviders, roomTypeNames })
    }
  }, [searchData, initialData])

  const uniqueRoomFacilities = initialData?.uniqueRoomFacilities || []
  const uniqueProviders = initialData?.uniqueProviders || []
  const roomTypeNames = initialData?.roomTypeNames || [
    { label: 'All', value: 'All' }
  ]

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      dir={lang === 'fa' ? 'rtl' : 'ltr'}
      classes={{ paper: classes.drawerPaper }}>
      <div className={classes.root}>
        <div className={classes.header}>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
          <Typography variant="h4" className={classes.filterTitle}>
            {getTranslate('Filters')}
          </Typography>
          <Typography className={classes.clearBtn} onClick={handleResetAll}>
            {getTranslate('Reset All')}
          </Typography>
        </div>
        <InputWithLabel
          placeholder={getTranslate('Enter Room Type Name')}
          inputSize="small"
          onChange={(event) => {
            setsearchInputValue(
              convertPersianNumbersToEnglishNumbers(event.currentTarget.value)
            )
          }}
          startIcon={<SearchIcon />}
        />
        <div className={classes.switchContainer}>
          <FormControlLabel
            control={
              <Switch
                checked={!searchAvailabilityInput.showUnavailableRatePlans}
                onChange={() =>
                  setSearchAvailabilityInput({
                    ...searchAvailabilityInput,
                    showUnavailableRatePlans:
                      !searchAvailabilityInput.showUnavailableRatePlans
                  })
                }
                color="primary"
                size="small"
              />
            }
            label={getTranslate('Show Available Rate Plans')}
            classes={{ label: classes.switchLabel }}
          />
        </div>
        <PriceSlider
          label={getTranslate('Price Range For 1 Night')}
          currency={searchAvailabilityInput.currency}
          min={min}
          max={maxPriceRange}
          price={priceRange}
          setPrice={handlePriceChange}
        />
        {roomTypeNames.length > 0 ? (
          <ContentFitCheckboxList
            header={getTranslate('Room Type ')}
            options={roomTypeNames}
            multiSelectedMode
            selectedItems={selectedRoomTypes}
            setSelectedItems={setselectedRoomTypes}
          />
        ) : (
          <></>
        )}
        {uniqueProviders.length > 0 ? (
          <CustomCheckBox
            header={getTranslate('Providers')}
            options={uniqueProviders}
            selectedItems={selectedProviders}
            setSelectedItems={setSelectedProviders}
            includesAll
          />
        ) : (
          <></>
        )}
        {uniqueRoomFacilities.length > 0 && (
          <CustomCheckBox
            header={getTranslate('Room Facilities')}
            options={uniqueRoomFacilities}
            selectedItems={selectedRoomFacilities}
            setSelectedItems={setSelectedRoomFacilities}
          />
        )}
      </div>
    </SwipeableDrawer>
  )
}

export default FilterDrawer
